import { useRef } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { ToggleGroup, ToggleGroupItem } from '../primitives/toggle-group.tsx';
import { SCROLL_EDGE_FADE, useScrollEdges } from './Tabs.tsx';
import { Tooltip } from './Tooltip.tsx';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Icon-only segment: the label becomes the tooltip and aria-label. */
  iconOnly?: boolean;
  count?: number;
  disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the group. */
  label: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * One of 2 to 5 views of the same content (list or board, 1h, 6h, 24h), on the shadcn
 * ToggleGroup in its segmented look (components.md, Selection controls): quiet text options,
 * the selected one on a soft fill, a count in a lighter tone. Never an action. Arrow keys move
 * between options; one is always selected.
 *
 * Every option has the same weight: selection shows only as the soft fill, hover only lifts the
 * text a step, focus only as the focus ring. A row wider than its column (a phone) scrolls inside
 * itself with a fade on the hidden edge, and the selected option scrolls into view.
 */
export function Segmented<T extends string>({ options, value, onChange, label, size = 'sm', className }: SegmentedProps<T>) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollEdges(ref, '[data-state="on"]');
  return (
    <ToggleGroup
      ref={ref}
      type="single"
      segmented
      aria-label={label}
      value={value}
      onValueChange={(v) => {
        // Pressing the selected segment again would clear it: a segmented control always has one.
        if (v) onChange(v as T);
      }}
      size={size === 'sm' ? 'sm' : 'default'}
      // Options never shrink under their labels (each is shrink-0); a column too narrow scrolls the row.
      className={cn('min-w-0 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain', SCROLL_EDGE_FADE, size === 'sm' ? 'h-control-sm' : 'h-control', className)}
      style={{ scrollbarWidth: 'none' }}
    >
      {options.map((o) => {
        const item = (
          <ToggleGroupItem
            key={o.value}
            value={o.value}
            disabled={o.disabled}
            aria-label={o.iconOnly ? o.label : undefined}
            className={cn(
              'group/seg h-full min-w-0 shrink-0 gap-1.5 font-medium hover:text-fg-muted focus-visible:-outline-offset-2 data-[state=on]:text-fg data-[state=on]:hover:text-fg',
              size === 'sm' ? 'text-caption' : 'px-3',
              '[&_svg:not([class*=size-])]:size-3.5',
            )}
          >
            {o.icon}
            {!o.iconOnly && o.label}
            {o.count != null && <span className="font-normal text-fg-subtle num group-aria-checked/seg:text-fg-muted">{o.count}</span>}
          </ToggleGroupItem>
        );
        return o.iconOnly ? (
          <Tooltip key={o.value} content={o.label}>
            {item}
          </Tooltip>
        ) : (
          item
        );
      })}
    </ToggleGroup>
  );
}
